import * as React from 'react';
import { useEffect } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase } from '../../../lib/supabase';

/**
 * Authentication context interface defining the shape of authentication-related data and methods
 * @interface AuthContextType
 */
interface AuthContextType {
  /** Current user session or null if not authenticated */
  session: Session | null;
  /** Loading state of the authentication context */
  loading: boolean;
  /** Sign in with email and password */
  signIn: (email: string, password: string) => Promise<void>;
  /** Create new account with email and password */
  signUp: (email: string, password: string) => Promise<void>;
  /** Sign out the current user */
  signOut: () => Promise<void>;
  /** Send password reset email */
  resetPassword: (email: string) => Promise<void>;
}

/*u* Athentication context for managing user session state */
export const AuthContext = React.createContext<AuthContextType | null>(null);

/**
 * Authentication provider component that manages user session state
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to be wrapped
 * @returns {JSX.Element} AuthProvider component
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = React.useState<Session | null>(null);
  const [loading, setLoading] = React.useState(true);

  // Function to recover session
  const recoverSession = async () => {
    try {
      const existingSession = window.localStorage.getItem('devpanc-auth-token');
      if (existingSession) {
        const parsedSession = JSON.parse(existingSession);
        if (parsedSession?.access_token) {
          const { data, error } = await supabase.auth.setSession({
            access_token: parsedSession.access_token,
            refresh_token: parsedSession.refresh_token,
          });
          if (!error && data.session) {
            setSession(data.session);
            return true;
          }
        }
      }
    } catch (error) {
      console.error('Error recovering session:', error);
    }
    return false;
  };

  useEffect(() => {
    let mounted = true;

    const initSession = async () => {
      try {
        // Try to recover session first
        const recovered = await recoverSession();
        if (recovered && mounted) {
          setLoading(false);
          return;
        }

        // If recovery failed, get fresh session
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        console.log('Initial session:', initialSession);
        if (mounted) {
          setSession(initialSession);
        }
      } catch (error) {
        console.error('Error getting initial session:', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
      console.log('Auth state change:', event, currentSession);
      
      if (mounted) {
        if (event === 'SIGNED_IN') {
          // Verify the session is valid
          const { data: { session: verifiedSession } } = await supabase.auth.getSession();
          setSession(verifiedSession);
          // Update localStorage
          if (verifiedSession) {
            window.localStorage.setItem('devpanc-auth-token', JSON.stringify({
              access_token: verifiedSession.access_token,
              refresh_token: verifiedSession.refresh_token,
            }));
          }
        } else if (event === 'SIGNED_OUT') {
          setSession(null);
          window.localStorage.removeItem('devpanc-auth-token');
        } else if (event === 'TOKEN_REFRESHED') {
          setSession(currentSession);
          if (currentSession) {
            window.localStorage.setItem('devpanc-auth-token', JSON.stringify({
              access_token: currentSession.access_token,
              refresh_token: currentSession.refresh_token,
            }));
          }
        } else {
          setSession(currentSession);
        }
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  /**
   * Sign in with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<void>} Sign in promise
   */
  const signIn = async (email: string, password: string) => {
    setLoading(true);
    try {
      const { error, data } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      if (data.session) {
        setSession(data.session);
        window.localStorage.setItem('devpanc-auth-token', JSON.stringify({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        }));
      }
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Create new account with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<void>} Sign up promise
   */
  const signUp = async (email: string, password: string) => {
    setLoading(true);
    try {
      const { error, data } = await supabase.auth.signUp({
        email,
        password,
      });
      if (error) throw error;
      if (data.session) {
        setSession(data.session);
        window.localStorage.setItem('devpanc-auth-token', JSON.stringify({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        }));
      }
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sign out the current user
   * @returns {Promise<void>} Sign out promise
   */
  const signOut = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setSession(null);
      window.localStorage.removeItem('devpanc-auth-token');
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Send password reset email
   * @param {string} email - User email
   * @returns {Promise<void>} Password reset promise
   */
  const resetPassword = async (email: string) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    session,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}
